import { Identify } from '@amplitude/analytics-browser';
import { LogLevel } from '@amplitude/analytics-types';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { useApplication } from '@bonusly/application';

import { ProductTier } from '../who-am-i/lib/company';
import { useIdentityStore } from '../who-am-i/lib/store';

interface Company {
	id: string;
	status: string;
	product_tier: string;
}

interface User {
	id: string;
	created_at: string;
	email: string;
	admin: boolean;
	global_admin: boolean;
	finance_admin: boolean;
	tech_admin: boolean;
	rewards_admin: boolean;
	awards_admin: boolean;
	announcements_admin: boolean;
	reports_admin: boolean;
	is_manager: boolean;
	locale: string;
	user_mode: string;
	custom_properties: Record<string, any>;
}

interface InitializeConfig {
	company: Company;
	user: User;
}

export const initializeAnalytics = ({ company, user }: InitializeConfig) => {
	const { analyticsClient: client, analyticsAPIKey, analyticsServerURL } = useIdentityStore.getState();
	const env = useApplication.getState().application.env;

	if (client.getUserId() || !analyticsAPIKey) {
		return;
	}

	// session replay for Amplitude during in trial only for engage
	const shouldEnableSessionReplay =
		env !== 'development' && company.status === 'in_trial' && company.product_tier === ProductTier.TierAlpha && user.email?.match(/@bonus\.ly$/) === null;

	if (shouldEnableSessionReplay) {
		const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 1 });
		client.add(sessionReplayTracking);
	}

	client.init(analyticsAPIKey, user.id, {
		logLevel: LogLevel.Error,
		autocapture: false,
		...(!!analyticsServerURL && {
			serverUrl: analyticsServerURL,
		}),
	});

	// Groups for amplitude feature flagging
	client.setGroup('companyId', company.id);
	client.setGroup('productTier', company.product_tier);

	const identity = new Identify();
	identity
		.set('created_at', new Date(user.created_at).getTime())
		.set('email', user.email)
		.set('is_admin', user.admin)
		.set('is_manager', user.is_manager)
		.set('surface', 'web')
		.set('translation_setting', user.locale)
		.set('user_mode', user.user_mode)
		.set('permissions', {
			global_admin: user.global_admin,
			finance_admin: user.finance_admin,
			tech_admin: user.tech_admin,
			rewards_admin: user.rewards_admin,
			awards_admin: user.awards_admin,
			announcements_admin: user.announcements_admin,
			reports_admin: user.reports_admin,
		});

	for (const prop of ['department', 'role']) {
		const value = user.custom_properties[prop];

		if (value) {
			identity.set(prop, value);
		}
	}

	client.identify(identity);
};

/**
 * Set (or overwrite) a user property. Used for mutable state signals
 * (e.g. seasonal_theme_active) that should always reflect the latest value.
 */
export const setUserProperty = (key: string, value: string | number | boolean): void => {
	const client = useIdentityStore.getState().analyticsClient;
	if (!client) return;

	const identity = new Identify();
	identity.set(key, value);
	client.identify(identity);
};

/**
 * Set a user property exactly once (first value wins). Used for stable
 * segmentation attributes that should not be overwritten by later activity.
 */
export const setUserPropertyOnce = (key: string, value: string | number | boolean): void => {
	const client = useIdentityStore.getState().analyticsClient;
	if (!client) return;

	const identity = new Identify();
	identity.setOnce(key, value);
	client.identify(identity);
};

export const shouldSuppressTracking = (): boolean => {
	const { company } = useIdentityStore.getState();
	return !!(company?.is_superadmin_initialized && company?.status === 'in_trial');
};

export const track = (event: string, data?: any) => {
	const client = useIdentityStore.getState().analyticsClient;
	const application = useApplication.getState().application;

	if (shouldSuppressTracking()) {
		return { promise: Promise.resolve() };
	}

	if (application.isDevelopment()) {
		console.info(`Tracking event: ${event}, data: ${JSON.stringify(data)}`);
	}

	return client?.track(event, data);
};

/**
 * Flush any queued Amplitude events. Call this before an operation that will
 * tear down the page (e.g. `window.location.reload()`) so recently-fired
 * events have a chance to reach Amplitude instead of being cancelled with
 * the in-flight request.
 *
 * Races against a short timeout so a slow/unreachable Amplitude never blocks
 * the caller indefinitely — the worst case is the same "event possibly lost"
 * behavior we have without flushing.
 */
export const flushAnalytics = async (timeoutMs = 1500): Promise<void> => {
	const client = useIdentityStore.getState().analyticsClient;
	const flushResult = client?.flush();
	if (!flushResult?.promise) return;

	await Promise.race([flushResult.promise, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
};
